import * as React from "react"
import { useState, useRef, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { t, Language, isRTL } from "@/lib/i18n"
import { AnimatePresence, motion } from "framer-motion"

export type TabbedNewsTabKey = "news"

export interface TabbedNewsProps {
  lang: Language
  newsItems?: string[]
}

const TAB_BG: Record<TabbedNewsTabKey, string> = {
  news: "bg-blue-600 hover:bg-blue-700",
}

const TAB_INACTIVE_BG: Record<TabbedNewsTabKey, string> = {
  news: "bg-blue-100 hover:bg-blue-200",
}

export function TabbedNews({ lang, newsItems }: TabbedNewsProps) {
  const [active, setActive] = useState<TabbedNewsTabKey>("news")
  const tabsRef = useRef<Record<TabbedNewsTabKey, HTMLButtonElement | null>>({ news: null })

  const labels: Record<TabbedNewsTabKey, string> = {
    news: t("news_tabs_announcements", lang),
  }

  const defaults: Record<TabbedNewsTabKey, string[]> = {
    news: [
      lang === "hi"
        ? "ग्रामीण हरियाणा में महिला उद्यमियों के लिए नई योजना शुरू।"
        : lang === "pa"
        ? "ਪਿੰਡੀਂ ਹਰਿਆਣਾ ਵਿੱਚ ਔਰਤ ਉਦਯਮੀਆਂ ਲਈ ਨਵੀਂ ਯੋਜਨਾ ਸ਼ੁਰੂ।"
        : "New scheme launched for women entrepreneurs in rural Haryana.",
      lang === "hi"
        ? "जिलों में आगामी ग्राम सभा बैठकों की घोषणा।"
        : lang === "pa"
        ? "ਜ਼ਿਲ੍ਹਿਆਂ ਵਿੱਚ ਆਉਣ ਵਾਲੀਆਂ ਗ੍ਰਾਮ ਸਭਾ ਬੈਠਕਾਂ ਦੀ ਘੋਸ਼ਣਾ।"
        : "Upcoming Gram Sabha meetings across districts announced.",
      lang === "hi"
        ? "अंबाला और कुरुक्षेत्र में बाढ़ राहत परियोजनाएँ शुरू।"
        : lang === "pa"
        ? "ਅੰਬਾਲਾ ਅਤੇ ਕੁਰੁਕਸ਼ੇਤਰ ਵਿੱਚ ਬਾੜ੍ਹ ਰਾਹਤ ਪ੍ਰੋਜੈਕਟ ਸ਼ੁਰੂ।"
        : "Flood relief projects initiated in Ambala and Kurukshetra.",
    ],
  }

  const items: Record<TabbedNewsTabKey, string[]> = {
    news: newsItems ?? defaults.news,
  }

  const order: TabbedNewsTabKey[] = ["news"]

  useEffect(() => {
    tabsRef.current[active]?.setAttribute("tabindex", "0")
  }, [active])

  const panelId = (key: TabbedNewsTabKey) => `tab-panel-${key}`
  const tabId = (key: TabbedNewsTabKey) => `tab-${key}`

  return (
    <Card className="bg-white shadow-lg">
      <CardContent className="p-4">
        <div
          role="tablist"
          aria-label={t("news_title", lang)}
          className="flex flex-wrap gap-2 mb-4"
        >
          {order.map((key) => {
            const isActive = active === key
            const activeStyles = `${TAB_BG[key]} text-white`
            const inactiveStyles = `${TAB_INACTIVE_BG[key]} text-gray-800`
            return (
              <button
                key={key}
                id={tabId(key)}
                role="tab"
                aria-selected={isActive}
                aria-controls={panelId(key)}
                tabIndex={isActive ? 0 : -1}
                ref={(el) => (tabsRef.current[key] = el)}
                className={`px-3 py-1 rounded-md text-[15px] font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-700 transition-colors ${
                  isActive ? activeStyles : inactiveStyles
                }`}
                onClick={() => setActive(key)}
              >
                {labels[key]}
              </button>
            )
          })}
        </div>

        <div className="min-h-[220px] md:min-h-[320px]" aria-live="polite">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              role="tabpanel"
              id={panelId(active)}
              aria-labelledby={tabId(active)}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="w-full"
            >
              <ul
                className="space-y-2 text-gray-700"
                aria-label={labels[active]}
                lang={lang}
                dir={isRTL(lang) ? "rtl" : "ltr"}
              >
                {items[active].map((line, i) => (
                  <li key={i} className="leading-relaxed">• {line}</li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </CardContent>
    </Card>
  )
}
